import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import styles from './Cage.module.css';

function Cage() {
    const navigate = useNavigate();
    const { id } = useParams();

    const [cages, setCages] = useState([]);
    const [aviaries, setAviaries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCage, setSelectedCage] = useState(null);
    const [selectedCageBirds, setSelectedCageBirds] = useState([]);
    
    // Estado para o campo de pesquisa
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTypeFilter, setSelectedTypeFilter] = useState('');

    // Controle de Modais
    const [isCageModalOpen, setIsCageModalOpen] = useState(false);
    const [isReproModalOpen, setIsReproModalOpen] = useState(false);
    const [isPostureModalOpen, setIsPostureModalOpen] = useState(false);
    const [availableBirds, setAvailableBirds] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    // Estados para o Modal de Finalização da Postura
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [offspringCount, setOffspringCount] = useState(0);
    const [finishFormData, setFinishFormData] = useState({
        posture_end: new Date().toISOString().split('T')[0],
        number_eggs: 0,
        comments: '',
    });
    const [offspringList, setOffspringList] = useState([]);

    // Forms
    const [cageForm, setCageForm] = useState({ 
        number: '', 
        title: '', 
        color: '',
        type: 'criadeira',
        height: '', 
        width: '', 
        depth: '', 
        aviaryId: '' 
    });
    
    function addDays(dateString, days) {
        if (!dateString) return null;
        const date = new Date(dateString);
        date.setDate(date.getDate() + days);
        return date;
    }

    function isDateReached(targetDate) {
        if (!targetDate) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(targetDate);
        target.setHours(0, 0, 0, 0);
        return today >= target;
    }

    const [reproForm, setReproForm] = useState({ reproduction_start: '', status: 'Ativo' });
    const [postureForm, setPostureForm] = useState({
        posture_start: '',
        first_egg: '',
        last_egg: '',
        number_eggs: 0,
        male: '',
        female: '',
        comments: ''
    });

    useEffect(() => {
        fetchBirds();
        fetchAviaries();

        if (id) {
            fetchCageById(id);
        } else {
            setSelectedCage(null);
            setSelectedCageBirds([]);
            fetchCages();
        }
    }, [id]);

    async function fetchCages() {
        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const res = await api.get('/cage/mycages', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCages(res.data.cages || []);
        } catch (error) {
            console.error('Erro ao buscar gaiolas:', error);
        } finally {
            setLoading(false);
        }
    }

    async function fetchCageById(cageId) {
        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const [resCage, resBirds] = await Promise.all([
                api.get(`/cage/${cageId}`, { headers: { Authorization: `Bearer ${token}` } }),
                api.get(`/cage/${cageId}/birds`, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            setSelectedCage(resCage.data.cage || resCage.data);
            setSelectedCageBirds(resBirds.data.birds || []);
        } catch (error) {
            console.error('Erro ao buscar gaiola ou aves:', error);
            setErrorMessage('Erro ao carregar dados da gaiola.');
            navigate('/cage');
        } finally {
            setLoading(false);
        }
    }

    async function fetchAviaries() {
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const res = await api.get('/aviary/myaviarys', {
                headers: { Authorization: `Bearer ${token}` }
            });
            const loadedAviaries = res.data.aviary || res.data.aviaries || res.data.aviarys || [];
            setAviaries(loadedAviaries);
        } catch (err) {
            console.error('Erro ao buscar aviários:', err);
        }
    }

    async function fetchBirds() {
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const res = await api.get('/bird/mybirds', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableBirds(res.data.birds || []);
        } catch (err) {
            console.error('Erro ao buscar aves:', err);
        }
    }

    const handleOpenFinishModal = () => {
        const activePosture = selectedCage?.activePosture;
        if (!activePosture) return;

        setFinishFormData({
            posture_end: activePosture.posture_end 
                ? new Date(activePosture.posture_end).toISOString().split('T')[0] 
                : new Date().toISOString().split('T')[0],
            number_eggs: activePosture.number_eggs || 0,
            comments: activePosture.comments || '',
        });

        setOffspringCount(0);
        setOffspringList([]);
        setIsFinishModalOpen(true);
    };

    const handleOffspringCountChange = (count) => {
        const num = Math.max(0, parseInt(count) || 0);
        setOffspringCount(num);

        const updatedList = Array.from({ length: num }, (_, index) => {
            return offspringList[index] || {
                name: `Filhote ${index + 1}`,
                bird_band: '',
                color: '',
                race: '',
                gender: 'male',
                birthday: new Date().toISOString().split('T')[0]
            };
        });

        setOffspringList(updatedList);
    };

    const handleOffspringChange = (index, field, value) => {
        const updated = [...offspringList];
        updated[index][field] = value;
        setOffspringList(updated);
    };

    const handleFinishSubmit = async (e) => {
        e.preventDefault();
        setErrorMessage('');
        const activePosture = selectedCage?.activePosture;

        try {
            const token = JSON.parse(localStorage.getItem('token'));

            await api.patch(`/posture/finish/${activePosture._id}`, {
                ...finishFormData,
                offsprings: offspringList
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Postura finalizada com sucesso!');
            setIsFinishModalOpen(false);
            if (id) fetchCageById(id);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Erro ao finalizar postura.');
        }
    };

    async function handleUpdatePostureDate(field, selectedDate) {
        setErrorMessage('');
        const activePosture = selectedCage?.activePosture;

        if (!activePosture?._id) {
            return setErrorMessage('Nenhuma postura ativa foi encontrada para ser atualizada.');
        }

        if (!selectedDate) return;

        try {
            const token = JSON.parse(localStorage.getItem('token'));
            
            await api.patch(`/posture/${activePosture._id}`, {
                [field]: selectedDate
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Data atualizada com sucesso!');
            if (id) fetchCageById(id);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Erro ao atualizar data da postura.');
        }
    }

    const checkOvoscopyNeeded = (lastEggDateStr) => {
        if (!lastEggDateStr) return { isNeeded: false, targetDate: null };
        
        const lastEggDate = new Date(lastEggDateStr);
        const targetDate = new Date(lastEggDate);
        targetDate.setDate(targetDate.getDate() + 5);
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkTarget = new Date(targetDate);
        checkTarget.setHours(0, 0, 0, 0);

        return {
            isNeeded: today >= checkTarget,
            targetDate: targetDate.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
        };
    };

    async function handleCreateCage(e) {
        e.preventDefault();
        setErrorMessage('');
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.post('/cage/create', cageForm, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert('Gaiola cadastrada com sucesso!');
            setIsCageModalOpen(false);
            setCageForm({ 
                number: '', 
                title: '', 
                color: '',
                type: 'criadeira',
                height: '', 
                width: '', 
                depth: '', 
                aviaryId: '' 
            });
            fetchCages();
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Erro ao cadastrar gaiola.');
        }
    }

    async function handleCreateReproduction(e) {
        e.preventDefault();
        setErrorMessage('');
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.post('/reproduction/create', {
                ...reproForm,
                cage: selectedCage._id
            }, { headers: { Authorization: `Bearer ${token}` } });

            alert('Reprodução iniciada com sucesso!');
            setIsReproModalOpen(false);
            setReproForm({ reproduction_start: '', status: 'Ativo' });
            if (id) fetchCageById(id);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Erro ao criar reprodução.');
        }
    }

    async function handleCreatePosture(e) {
        e.preventDefault();
        setErrorMessage('');
        if (!selectedCage?.activeReproduction) {
            return setErrorMessage('Não há reprodução ativa nesta gaiola.');
        }
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            await api.post('/posture/create', {
                ...postureForm,
                reproduction: selectedCage.activeReproduction._id
            }, { headers: { Authorization: `Bearer ${token}` } });

            alert('Postura registrada com sucesso!');
            setIsPostureModalOpen(false);
            setPostureForm({
                posture_start: '',
                first_egg: '',
                last_egg: '',
                number_eggs: 0,
                male: '',
                female: '',
                comments: ''
            });
            if (id) fetchCageById(id);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Erro ao registrar postura.');
        }
    }

    const filteredCages = cages.filter(cage => {
        const term = searchTerm.toLowerCase();
        
        const cageNumber = String(cage.number || '').toLowerCase();
        const cageTitle = (cage.title || '').toLowerCase();
        const aviaryName = (cage.aviary?.name || cage.aviary?.title || cage.aviary?.nome || '').toLowerCase();

        const matchesType = selectedTypeFilter === '' || cage.type === selectedTypeFilter;

        const hasMatchingBird = cage.birds?.some(bird => {
            const name = (bird.name || '').toLowerCase();
            const bandRight = (bird.bird_band_right || '').toLowerCase();
            const bandLeft = (bird.bird_band_left || '').toLowerCase();
            return name.includes(term) || bandRight.includes(term) || bandLeft.includes(term);
        });

        const matchesSearch = (
            cageNumber.includes(term) ||
            cageTitle.includes(term) ||
            aviaryName.includes(term) ||
            hasMatchingBird
        );

        return matchesType && matchesSearch;
    });

    if (loading) return <p className={styles.loading}>Carregando gaiolas...</p>;

    // VISÃO DE DETALHES
    if (selectedCage) {
        const activeRepro = selectedCage.activeReproduction;
        const activePosture = selectedCage.activePosture;
        const male = activePosture?.male || selectedCage.male;
        const female = activePosture?.female || selectedCage.female;
        const ovoscopyInfo = checkOvoscopyNeeded(activePosture?.last_egg);

        return (
            <div className={styles.container}>
                {errorMessage && (
                    <div className={styles.error_alert_overlay}>
                        <div className={styles.error_alert_box}>
                            <div className={styles.error_alert_icon}>⚠️</div>
                            <h3>Atenção</h3>
                            <p>{errorMessage}</p>
                            <button 
                                type="button" 
                                className={styles.error_alert_btn} 
                                onClick={() => setErrorMessage('')}
                            >
                                Entendi
                            </button>
                        </div>
                    </div>
                )}

                <button className={styles.back_button} onClick={() => navigate('/cage')}>
                    ← Voltar para gaiolas
                </button>

                {/* Header Gaiola */}
                <div className={styles.cage_detail_header}>
                    <div className={styles.cage_number_box_large}>
                        {String(selectedCage.number).padStart(2, '0')}
                    </div>
                    <div className={styles.cage_header_info}>
                        <h2>{selectedCage.title}</h2>
                        <p>{male && female ? `${male.name} × ${female.name}` : 'Sem casal atribuído'}</p>
                        {(selectedCage.aviary?.name || selectedCage.aviary?.title) && (
                            <small>Aviário: {selectedCage.aviary?.name || selectedCage.aviary?.title}</small>
                        )}
                    </div>
                    <div className={styles.badges_wrapper}>
                        <span className={activeRepro ? styles.badge_repro : styles.badge_descanso}>
                            {activeRepro ? 'Em Reprodução' : 'Em Descanso'}
                        </span>
                        {ovoscopyInfo.isNeeded && (
                            <span className={styles.badge_ovoscopia}>⚠ Ovoscopia</span>
                        )}
                    </div>
                </div>

                {/* Barra de Ações */}
                {(!activeRepro || !activePosture) && (
                    <div className={styles.actions_bar}>
                        {!activeRepro && (
                            <button className={styles.btn_action} onClick={() => setIsReproModalOpen(true)}>
                                + Nova Reprodução
                            </button>
                        )}
                        {activeRepro && !activePosture && (
                            <button 
                                className={styles.btn_action_secondary} 
                                onClick={() => setIsPostureModalOpen(true)}
                            >
                                + Nova Postura
                            </button>
                        )}
                    </div>
                )}

                {/* Aves na Gaiola */}
                <div className={styles.section_box}>
                    <h3>🐦 Aves na Gaiola ({selectedCageBirds.length})</h3>
                    {selectedCageBirds.length > 0 ? (
                        <div className={styles.couple_grid}>
                            {selectedCageBirds.map(bird => (
                                <div key={bird._id} className={styles.parent_card}>
                                    <span>{bird.gender === 'male' ? 'MACHO' : bird.gender === 'female' ? 'FÊMEA' : 'INDEFINIDO'}</span>
                                    <strong>{bird.name}</strong>
                                    <button 
                                        className={styles.link_button} 
                                        onClick={() => navigate(`/bird/${bird._id}`)}
                                    >
                                        Ver ficha da ave →
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.empty_subtext}>Nenhuma ave na gaiola no momento.</p>
                    )}
                </div>

                {/* Casal da Postura */}
                <div className={styles.section_box}>
                    <h3>💕 Casal da Postura</h3>
                    <div className={styles.couple_grid}>
                        <div className={styles.parent_card}>
                            <span>MACHO DA POSTURA</span>
                            <strong>{male?.name || 'Não definido'}</strong>
                            <p>Anilha: {male?.bird_band || '-'} • {male?.color || ''}</p>
                            {male?._id && (
                                <button 
                                    className={styles.link_button} 
                                    onClick={() => navigate(`/bird/${male._id}`)}
                                >
                                    Ver ficha da ave →
                                </button>
                            )}
                        </div>
                        <div className={styles.parent_card}>
                            <span>FÊMEA DA POSTURA</span>
                            <strong>{female?.name || 'Não definida'}</strong>
                            <p>Anilha: {female?.bird_band || '-'} • {female?.color || ''}</p>
                            {female?._id && (
                                <button 
                                    className={styles.link_button} 
                                    onClick={() => navigate(`/bird/${female._id}`)}
                                >
                                    Ver ficha da ave →
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Seção Ovoscopia */}
                {activePosture?.last_egg && (
                    <div className={styles.section_box}>
                        <h3>🔎 Ovoscopia</h3>
                        {ovoscopyInfo.isNeeded ? (
                            <div className={styles.alert_banner_warning}>
                                ⚠️ <strong>Ovoscopia recomendada</strong>
                                <p>Último ovo: {new Date(activePosture.last_egg).toLocaleDateString('pt-BR', { timeZone: 'UTC' })} • Data prevista: {ovoscopyInfo.targetDate}</p>
                            </div>
                        ) : (
                            <div className={styles.alert_banner_info}>
                                ℹ️ Ovoscopia agendada para {ovoscopyInfo.targetDate} (5 dias após o último ovo).
                            </div>
                        )}
                    </div>
                )}

                {/* Resumo da Reprodução Atual */}
                {activeRepro && (() => {
                    const currentPostures = activeRepro.postures || [];
                    const totalEggs = currentPostures.reduce(
                        (acc, p) => acc + (p.number_eggs || p.eggsCount || 0),
                        0
                    );
                    const allCurrentOffspring = currentPostures.flatMap(p => p.offspring || []);
                    const totalOffspring = allCurrentOffspring.length;
                    const successRate = totalEggs > 0 ? Math.round((totalOffspring / totalEggs) * 100) : 0;

                    return (
                        <div className={styles.section_box}>
                            <h3>📊 Reprodução atual</h3>
                            <div className={styles.metrics_grid}>
                                <div className={styles.metric_card}>
                                    <h2>{totalEggs}</h2>
                                    <span>Ovos</span>
                                </div>
                                <div className={styles.metric_card}>
                                    <h2>{totalOffspring}</h2>
                                    <span>Filhotes</span>
                                </div>
                                <div className={styles.metric_card}>
                                    <h2>{successRate}%</h2>
                                    <span>Taxa de Sucesso</span>
                                </div>
                                <div className={styles.metric_card}>
                                    <h2>
                                        {activeRepro.reproduction_start
                                            ? new Date(activeRepro.reproduction_start).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                            : '-'}
                                    </h2>
                                    <span>Início Preparação</span>
                                </div>
                                <div className={styles.metric_card}>
                                    <h2>
                                        {activeRepro.reproduction_end
                                            ? new Date(activeRepro.reproduction_end).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                            : '-'}
                                    </h2>
                                    <span>Final Preparação</span>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* Linha do tempo da postura atual */}
                {activePosture && (
                    <div className={styles.section_box}>
                        <h3>⏱️ Linha do tempo Postura Atual</h3>
                        <div className={styles.timeline}>
                            <div className={styles.timeline_item}>
                                <div className={styles.timeline_dot} />
                                <div className={styles.timeline_content}>
                                    <strong>{activePosture.posture_start ? new Date(activePosture.posture_start).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                                    <h4>Início da postura</h4>
                                    <p>Casal colocado na gaiola de reprodução.</p>
                                    <div className={styles.date_picker_wrapper}>
                                        <button 
                                            type="button"
                                            className={styles.btn_timeline_action}
                                            onClick={(e) => {
                                                const input = e.currentTarget.nextElementSibling;
                                                if (input && input.showPicker) input.showPicker();
                                            }}
                                        >
                                            {activePosture.posture_start ? ' Alterar Data' : '+ Definir Data'}
                                        </button>
                                        <input 
                                            type="date" 
                                            className={styles.hidden_date_input}
                                            onChange={(e) => handleUpdatePostureDate('posture_start', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.timeline_item}>
                                <div className={styles.timeline_dot} />
                                <div className={styles.timeline_content}>
                                    <strong>{activePosture?.first_egg ? new Date(activePosture.first_egg).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                                    <h4>Primeiro ovo</h4>
                                    <p>Primeiro ovo identificado no ninho.</p>
                                    <div className={styles.date_picker_wrapper}>
                                        <button 
                                            type="button"
                                            className={styles.btn_timeline_action}
                                            onClick={(e) => {
                                                const input = e.currentTarget.nextElementSibling;
                                                if (input && input.showPicker) input.showPicker();
                                            }}
                                        >
                                            {activePosture?.first_egg ? ' Alterar Data' : '+ Definir Data'}
                                        </button>
                                        <input 
                                            type="date" 
                                            className={styles.hidden_date_input}
                                            onChange={(e) => handleUpdatePostureDate('first_egg', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.timeline_item}>
                                <div className={styles.timeline_dot} />
                                <div className={styles.timeline_content}>
                                    <strong>{activePosture?.last_egg ? new Date(activePosture.last_egg).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                                    <h4>Último ovo</h4>
                                    <p>Último ovo identificado no ninho.</p>
                                    <div className={styles.date_picker_wrapper}>
                                        <button 
                                            type="button"
                                            className={styles.btn_timeline_action}
                                            onClick={(e) => {
                                                const input = e.currentTarget.nextElementSibling;
                                                if (input && input.showPicker) input.showPicker();
                                            }}
                                        >
                                            {activePosture?.last_egg ? ' Alterar Data' : '+ Definir Data'}
                                        </button>
                                        <input 
                                            type="date" 
                                            className={styles.hidden_date_input}
                                            onChange={(e) => handleUpdatePostureDate('last_egg', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.timeline_item}>
                                <div className={styles.timeline_dot} />
                                <div className={styles.timeline_content}>
                                    <strong>{activePosture?.choco_start ? new Date(activePosture.choco_start).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                                    <h4>Início da choca</h4>
                                    <p>Fêmea iniciou a choca regular.</p>
                                    <div className={styles.date_picker_wrapper}>
                                        <button 
                                            type="button"
                                            className={styles.btn_timeline_action}
                                            onClick={(e) => {
                                                const input = e.currentTarget.nextElementSibling;
                                                if (input && input.showPicker) input.showPicker();
                                            }}
                                        >
                                            {activePosture?.choco_start ? ' Alterar Data' : '+ Definir Data'}
                                        </button>
                                        <input 
                                            type="date" 
                                            className={styles.hidden_date_input}
                                            onChange={(e) => handleUpdatePostureDate('choco_start', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.timeline_item}>
                                <div className={styles.timeline_dot} />
                                <div className={styles.timeline_content}>
                                    <strong>{activePosture?.first_offspring ? new Date(activePosture.first_offspring).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                                    <h4>Primeiro filhote</h4>
                                    <p>Primeiro filhote nasceu.</p>
                                    <div className={styles.date_picker_wrapper}>
                                        <button 
                                            type="button"
                                            className={styles.btn_timeline_action}
                                            onClick={(e) => {
                                                const input = e.currentTarget.nextElementSibling;
                                                if (input && input.showPicker) input.showPicker();
                                            }}
                                        >
                                            {activePosture?.first_offspring ? ' Alterar Data' : '+ Definir Data'}
                                        </button>
                                        <input 
                                            type="date" 
                                            className={styles.hidden_date_input}
                                            onChange={(e) => handleUpdatePostureDate('first_offspring', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className={styles.timeline_item}>
                                <div className={styles.timeline_dot} />
                                <div className={styles.timeline_content}>
                                    <strong>{activePosture?.posture_end ? new Date(activePosture.posture_end).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                                    <h4>Final da postura</h4>
                                    <p>Encerramento do ciclo da postura atual.</p>
                                    <div className={styles.date_picker_wrapper}>
                                        <button 
                                            type="button"
                                            className={styles.btn_timeline_action}
                                            onClick={(e) => {
                                                const input = e.currentTarget.nextElementSibling;
                                                if (input && input.showPicker) input.showPicker();
                                            }}
                                        >
                                            {activePosture?.posture_end ? ' Alterar Data' : '+ Definir Data'}
                                        </button>
                                        <input 
                                            type="date" 
                                            className={styles.hidden_date_input}
                                            onChange={(e) => handleUpdatePostureDate('posture_end', e.target.value)}
                                        />
                                        
                                        {activePosture?.posture_end && (
                                            <button 
                                                type="button" 
                                                className={styles.btn_finish_posture}
                                                onClick={handleOpenFinishModal}
                                            >
                                                Finalizar Postura
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Histórico de reproduções passadas */}
                {selectedCage.reproductions?.length > 0 ? (
                    selectedCage.reproductions.map(repro => {
                        const sortedPostures = repro.postures ? [...repro.postures].sort((a, b) => {
                            const dateA = new Date(a.posture_start || a.start_date || a.createdAt || 0);
                            const dateB = new Date(b.posture_start || b.start_date || b.createdAt || 0);
                            return dateA - dateB;
                        }) : [];

                        const maleName = repro.male?.name || male?.name || 'Macho';
                        const femaleName = repro.female?.name || female?.name || 'Fêmea';

                        return (
                            <div key={repro._id} className={styles.history_card}>
                                <div className={styles.postures_history_wrapper}>
                                    <h3>📋 Detalhamento por Postura</h3>
                                    
                                    {sortedPostures.length > 0 ? (
                                        sortedPostures.map((posture, idx) => {
                                            const startDate = posture.posture_start || posture.start_date;
                                            const endDate = posture.posture_end || posture.end_date;

                                            return (
                                                <div key={posture._id || idx} className={styles.posture_history_item}>
                                                    <div className={styles.posture_history_header}>
                                                        <strong>Postura #{idx + 1}</strong>
                                                        <span>
                                                            Ovos: <b>{posture.number_eggs || posture.eggsCount || 0}</b> | Filhotes: <b>{posture.offspring?.length || 0}</b>
                                                        </span>
                                                    </div>

                                                    <div className={styles.posture_info_details}>
                                                        <p>👥 <b>Casal:</b> {maleName} (♂) × {femaleName} (♀)</p>
                                                        <p>
                                                            📅 <b>Período:</b> {startDate ? new Date(startDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : 'N/A'} 
                                                            {endDate ? ` até ${new Date(endDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}` : ' (Em andamento)'}
                                                        </p>
                                                    </div>

                                                    <div className={styles.history_offspring_grid}>
                                                        {posture.offspring?.length > 0 ? (
                                                            posture.offspring.map(child => (
                                                                <div key={child._id || child.bird_band} className={styles.mini_child_card}>
                                                                    <span>{child.gender === 'male' ? '♂' : '♀'}</span>
                                                                    <div>
                                                                        <strong>{child.name}</strong>
                                                                        <p>Anilha: {child.bird_band} - {child.color}</p>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <p className={styles.empty_subtext}>Nenhum filhote cadastrado nesta postura.</p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <p className={styles.empty_subtext}>Nenhuma postura registrada nesta reprodução.</p>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className={styles.empty_text}>Sem histórico para esta gaiola.</p>
                )}

                {/* Modais */}
                {isReproModalOpen && (
                    <div className={styles.modal_overlay}>
                        <div className={styles.modal_content}>
                            <h3>Nova Reprodução</h3>
                            <form onSubmit={handleCreateReproduction} className={styles.form}>
                                <label>Data de Início *</label>
                                <input 
                                    type="date" 
                                    value={reproForm.reproduction_start}
                                    onChange={(e) => setReproForm({...reproForm, reproduction_start: e.target.value})}
                                    required 
                                />
                                <div className={styles.modal_actions}>
                                    <button type="button" onClick={() => setIsReproModalOpen(false)} className={styles.btn_cancel}>Cancelar</button>
                                    <button type="submit" className={styles.btn_submit}>Salvar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {isFinishModalOpen && (
                    <div className={styles.modal_overlay}>
                        <div className={styles.modal_content} style={{ maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto' }}>
                            <h3>Finalizar Postura e Registrar Filhotes</h3>

                            <form onSubmit={handleFinishSubmit} className={styles.form}>
                                <h4>Dados Gerais da Postura</h4>
                                
                                <label>Data de Término da Postura:
                                    <input 
                                        type="date" 
                                        value={finishFormData.posture_end}
                                        onChange={(e) => setFinishFormData({ ...finishFormData, posture_end: e.target.value })}
                                        required
                                    />
                                </label>

                                <label>Quantidade Total de Ovos:
                                    <input 
                                        type="number" 
                                        min="0"
                                        value={finishFormData.number_eggs}
                                        onChange={(e) => setFinishFormData({ ...finishFormData, number_eggs: e.target.value })}
                                    />
                                </label>

                                <label>Observações/Comentários:
                                    <input 
                                        type="text" 
                                        value={finishFormData.comments}
                                        onChange={(e) => setFinishFormData({ ...finishFormData, comments: e.target.value })}
                                    />
                                </label>

                                <hr style={{ margin: '16px 0', borderColor: '#e5e7eb' }} />

                                <h4>Filhotes Nascidos</h4>
                                <label>Quantos filhotes nasceram nesta postura?
                                    <input 
                                        type="number" 
                                        min="0" 
                                        value={offspringCount}
                                        onChange={(e) => handleOffspringCountChange(e.target.value)}
                                    />
                                </label>

                                {offspringList.map((child, index) => (
                                    <div key={index} style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', marginTop: '10px', border: '1px solid #e5e7eb' }}>
                                        <h5>🐥 Filhote #{index + 1}</h5>
                                        <div className={styles.offspring_form_grid}>
                                            <label>Nome:
                                                <input 
                                                    type="text" 
                                                    value={child.name} 
                                                    onChange={(e) => handleOffspringChange(index, 'name', e.target.value)} 
                                                    required 
                                                />
                                            </label>

                                            <label>Anilha:
                                                <input 
                                                    type="text" 
                                                    value={child.bird_band} 
                                                    onChange={(e) => handleOffspringChange(index, 'bird_band', e.target.value)} 
                                                    required 
                                                />
                                            </label>

                                            <label>Cor:
                                                <input 
                                                    type="text" 
                                                    value={child.color} 
                                                    onChange={(e) => handleOffspringChange(index, 'color', e.target.value)} 
                                                    required 
                                                />
                                            </label>

                                            <label>Raça:
                                                <input 
                                                    type="text" 
                                                    value={child.race} 
                                                    onChange={(e) => handleOffspringChange(index, 'race', e.target.value)} 
                                                    required 
                                                />
                                            </label>

                                            <label>Sexo:
                                                <select 
                                                    value={child.gender} 
                                                    onChange={(e) => handleOffspringChange(index, 'gender', e.target.value)}
                                                >
                                                    <option value="male">Macho</option>
                                                    <option value="female">Fêmea</option>
                                                </select>
                                            </label>

                                            <label>Data Nascimento:
                                                <input 
                                                    type="date" 
                                                    value={child.birthday} 
                                                    onChange={(e) => handleOffspringChange(index, 'birthday', e.target.value)} 
                                                    required 
                                                />
                                            </label>
                                        </div>
                                    </div>
                                ))}

                                <div className={styles.modal_actions}>
                                    <button type="button" className={styles.btn_cancel} onClick={() => setIsFinishModalOpen(false)}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className={styles.btn_submit}>
                                        Salvar e Encerrar Postura
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {isPostureModalOpen && (
                    <div className={styles.modal_overlay}>
                        <div className={styles.modal_content}>
                            <h3>Nova Postura</h3>
                            <form onSubmit={handleCreatePosture} className={styles.form}>
                                <label>Macho</label>
                                <select 
                                    value={postureForm.male} 
                                    onChange={(e) => setPostureForm({...postureForm, male: e.target.value})}
                                >
                                    <option value="">Selecione o Macho</option>
                                    {availableBirds.filter(b => b.gender === 'male').map(b => (
                                        <option key={b._id} value={b._id}>{b.name} ({b.bird_band})</option>
                                    ))}
                                </select>

                                <label>Fêmea</label>
                                <select 
                                    value={postureForm.female} 
                                    onChange={(e) => setPostureForm({...postureForm, female: e.target.value})}
                                >
                                    <option value="">Selecione a Fêmea</option>
                                    {availableBirds.filter(b => b.gender === 'female').map(b => (
                                        <option key={b._id} value={b._id}>{b.name} ({b.bird_band})</option>
                                    ))}
                                </select>

                                <label>Data de Início *</label>
                                <input 
                                    type="date" 
                                    value={postureForm.posture_start} 
                                    onChange={(e) => setPostureForm({...postureForm, posture_start: e.target.value})} 
                                    required 
                                />

                                <div className={styles.modal_actions}>
                                    <button type="button" onClick={() => setIsPostureModalOpen(false)} className={styles.btn_cancel}>Cancelar</button>
                                    <button type="submit" className={styles.btn_submit}>Salvar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // VISÃO DE LISTAGEM GERAL
    return (
        <div className={styles.container}>
            {errorMessage && (
                <div className={styles.error_alert_overlay}>
                    <div className={styles.error_alert_box}>
                        <div className={styles.error_alert_icon}>⚠️</div>
                        <h3>Atenção</h3>
                        <p>{errorMessage}</p>
                        <button 
                            type="button" 
                            className={styles.error_alert_btn} 
                            onClick={() => setErrorMessage('')}
                        >
                            Entendi
                        </button>
                    </div>
                </div>
            )}

            <div className={styles.top_header}>
                <div>
                    <span className={styles.badge_category}>CONTROLE</span>
                    <h1>Minhas gaiolas</h1>
                    <p className={styles.subtitle}>Acompanhe a reprodução de cada casal.</p>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button className={styles.btn_action} onClick={() => setIsCageModalOpen(true)}>
                        <span>+ Nova</span>
                        <span>Gaiola</span>
                    </button>

                    <div className={styles.count_box}>
                        <span className={styles.count_number}>{filteredCages.length}</span>
                        <span className={styles.count_label}>gaiolas</span>
                    </div>
                </div>
            </div>

            {/* Campo de Busca */}
            <div className={styles.search_bar}>
                <input 
                    type="text" 
                    placeholder="Pesquisar por nº, aviário, anilha ou nome do casal..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.search_input}
                />
            </div>

            {/* Filtro de Tipo */}
            <h4>Filtrar por tipo:</h4>
            <div className={styles.search_bar}>
                <select
                    value={selectedTypeFilter}
                    onChange={(e) => setSelectedTypeFilter(e.target.value)}
                    className={styles.search_input}
                    style={{ width: '100%' }}
                >
                    <option value="">Todos os Tipos</option>
                    <option value="criadeira">Criadeira</option>
                    <option value="voadeira">Voadeira</option>
                    <option value="individual">Individual</option>
                </select>
            </div>

            <div className={styles.cages_list}>
                {filteredCages.length > 0 ? (
                    filteredCages.map((cage) => {
                        const activeRepro = cage.activeReproduction;
                        const activePosture = cage.activePosture;

                        // Cálculos das pendências específicas desta gaiola
                        const isOvoscopyPending = activePosture?.choco_start && 
                            !activePosture?.ovoscopy_done && 
                            isDateReached(addDays(activePosture.choco_start, 6));

                        const isHatchPending = activePosture?.choco_start && 
                            !activePosture?.hatch_done && 
                            isDateReached(addDays(activePosture.choco_start, 13));

                        const isRingPending = activePosture?.first_offspring && 
                            !activePosture?.ring_done && 
                            isDateReached(addDays(activePosture.first_offspring, 5));

                        // Casal da Postura Ativa
                        const postureMale = activePosture?.male;
                        const postureFemale = activePosture?.female;

                        // IDs dos pais na postura (se existirem) para ignorar na renderização da lista
                        const postureMaleId = postureMale?._id || postureMale;
                        const postureFemaleId = postureFemale?._id || postureFemale;

                        // Filtra as aves para exibir na lista apenas as que NÃO são nem o macho nem a fêmea da postura
                        const displayBirds = (cage.birds || []).filter((bird) => {
                            const birdId = bird._id || bird;
                            return birdId !== postureMaleId && birdId !== postureFemaleId;
                        });

                        return (
                            <div 
                                key={cage._id} 
                                className={styles.cage_row_card} 
                                onClick={() => navigate(`/cage/${cage._id}`)}
                            >
                                
                                <div className={styles.cage_top_header}>
                                    <div className={styles.cage_number_box}>
                                        {String(cage.number).padStart(2, '0')}
                                    </div>

                                    <div className={styles.cage_status_group} style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
                                        <span className={activeRepro ? styles.badge_repro : styles.badge_descanso}>
                                            {activeRepro ? 'EM REPRODUÇÃO' : 'EM DESCANSO'}
                                        </span>

                                        {/* Badge de Ovoscopia Pendente */}
                                        {isOvoscopyPending && (
                                            <span className={styles.badge_ovoscopia}>
                                                ⚠ Ovoscopia
                                            </span>
                                        )}

                                        {/* Badge de Nascimento Pendente */}
                                        {isHatchPending && (
                                            <span className={styles.badge_ovoscopia} style={{ backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }}>
                                                ⚠ Nascimento
                                            </span>
                                        )}

                                        {/* Badge de Anilhamento Pendente */}
                                        {isRingPending && (
                                            <span className={styles.badge_ovoscopia} style={{ backgroundColor: '#e0e7ff', color: '#3730a3', borderColor: '#c7d2fe' }}>
                                                ⚠ Anilhamento
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.cage_info}>
                                    <h3>{cage.title} - {cage.type.charAt(0).toUpperCase() + cage.type.slice(1)}</h3>
                                    
                                    {/* Exibição do Casal da Postura (se existir) */}
                                    {activePosture && (
                                        <p className={styles.couple_info}>
                                            <strong>Casal da Postura: </strong>
                                            {postureMale && postureFemale 
                                                ? `${postureMale.name} × ${postureFemale.name}` 
                                                : 'Não informado'}
                                        </p>
                                    )}

                                    {/* Lista de Aves na Gaiola */}
                                    <div className={styles.birds_list_container}>
                                        <span className={styles.birds_label}>
                                            Outras Aves na Gaiola (Total: {cage.birds?.length || 0}):
                                        </span>
                                        {displayBirds.length > 0 ? (
                                            <ul className={styles.birds_list}>
                                                {displayBirds.map((bird) => {
                                                    const bands = [bird.bird_band_left, bird.bird_band_right].filter(Boolean);
                                                    const band = bands.length > 0 ? bands.join(' / ') : 'Sem anilha';
                                                    const genderSymbol = bird.gender === 'male' ? '♂' : bird.gender === 'female' ? '♀' : '?';
                                                    return (
                                                        <li key={bird._id} className={styles.bird_item}>
                                                            <span className={styles.bird_gender}>{genderSymbol}</span>
                                                            <span className={styles.bird_name}>{bird.name}</span>
                                                            <span className={styles.bird_band}>({band})</span>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p className={styles.empty_birds}>
                                                {activePosture 
                                                    ? 'Apenas o casal de postura presente nesta gaiola.' 
                                                    : 'Nenhuma ave nesta gaiola.'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <p className={styles.empty_text}>Nenhuma gaiola encontrada com esses termos.</p>
                )}
            </div>

            {/* Modal Nova Gaiola */}
            {isCageModalOpen && (
                <div className={styles.modal_overlay}>
                    <div className={styles.modal_content}>
                        <h3>Nova Gaiola</h3>
                        <form onSubmit={handleCreateCage} className={styles.form}>
                            <label>Aviário *</label>
                            <select 
                                value={cageForm.aviaryId}
                                onChange={(e) => setCageForm({ ...cageForm, aviaryId: e.target.value })}
                                required
                            >
                                <option value="">
                                    {aviaries.length === 0 ? 'Nenhum aviário encontrado' : 'Selecione um Aviário'}
                                </option>
                                {aviaries.map((aviary) => {
                                    const id = aviary._id || aviary.id;
                                    const name = aviary.name || aviary.title || aviary.nome || aviary.description || `Aviário ${id}`;

                                    return (
                                        <option key={id} value={id}>
                                            {name}
                                        </option>
                                    );
                                })}
                            </select>

                            <label>Número *</label>
                            <input 
                                type="number" 
                                inputMode="numeric"
                                placeholder="Ex: 1"
                                value={cageForm.number}
                                onChange={(e) => setCageForm({...cageForm, number: e.target.value})}
                                required 
                            />

                            <label>Título / Identificação *</label>
                            <input 
                                type="text" 
                                placeholder="Ex: Gaiola 01"
                                value={cageForm.title}
                                onChange={(e) => setCageForm({...cageForm, title: e.target.value})}
                                required 
                            />

                            <label>Tipo *</label>
                            <select name="gender" value={cageForm.type} onChange={(e) => setCageForm({...cageForm, type: e.target.value})} required>
                                <option value="criadeira">Criadeira</option>
                                <option value="voadeira">Voadeira</option>
                                <option value="individual">Individual</option>
                            </select>

                            <label>Cor *</label>
                            <input 
                                type="text" 
                                placeholder="Ex: Branca, Epóxi, Prata"
                                value={cageForm.color}
                                onChange={(e) => setCageForm({...cageForm, color: e.target.value})}
                                required 
                            />

                            <div className={styles.modal_actions}>
                                <button type="button" onClick={() => setIsCageModalOpen(false)} className={styles.btn_cancel}>
                                    Cancelar
                                </button>
                                <button type="submit" className={styles.btn_submit}>
                                    Salvar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Cage;